// Claude API Configuration. The local proxy owns ANTHROPIC_API_KEY; never put
// provider credentials in browser-delivered JavaScript.
const CLAUDE_API_URL = 'http://localhost:3011/api/claude'; // Use local CORS proxy
const CLAUDE_MODEL = 'claude-sonnet-4-20250514'; // Claude Sonnet 4

// Standard inventory categories for restaurant/food service
const INVENTORY_CATEGORIES = [
  // Food Inventory
  'Produce',
  'Meat & Poultry', 
  'Seafood',
  'Dairy',
  'Dry Goods',
  'Bakery',
  'Frozen Foods',
  'Prepared Foods',
  
  // Beverage Inventory
  'Alcohol - Beer',
  'Alcohol - Wine', 
  'Alcohol - Liquor/Spirits',
  'Non-Alcoholic Beverages',
  
  // Bar Inventory
  'Garnishes',
  'Mixes & Syrups',
  'Glassware',
  
  // Paper & Packaging
  'Takeout Containers',
  'Napkins & Paper Towels',
  'Cups, Lids, Straws',
  'To-Go Bags',
  
  // Cleaning & Kitchen Supplies
  'Cleaning Supplies',
  'Kitchen Supplies'
];

// Default store locations (will be loaded from user's store settings)
const DEFAULT_LOCATIONS = [
  'Walk-in Cooler',
  'Walk-in Freezer', 
  'Kitchen Prep Area',
  'Kitchen Line',
  'Dry Storage',
  'Bar Area',
  'Coffee Station',
  'Dish Pit',
  'Office/Storage',
  'Dining Room'
];

/**
 * Call OpenAI API to categorize and assign locations to inventory items
 * @param {Array} items - Array of inventory items with descriptions
 * @param {Array} userLocations - User's custom locations from store settings
 * @returns {Promise<Array>} - Items with AI-assigned categories and locations
 */
export const categorizeInventoryItems = async (items, userLocations = DEFAULT_LOCATIONS) => {
  try {
    const prompt = createCategorizationPrompt(items, userLocations);
    
    const response = await fetch(CLAUDE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: CLAUDE_MODEL,
        max_tokens: 64000, // Maximum output tokens for Claude Sonnet 4
        temperature: 0.2,
        messages: [
          {
            role: 'user', 
            content: prompt
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`Claude API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const aiResponse = data.content[0].text;
    
    return parseAIResponse(aiResponse, items);
    
  } catch (error) {
    console.error('Error calling Claude API:', error);
    throw error;
  }
};

/**
 * Create a structured prompt for OpenAI categorization
 */
const createCategorizationPrompt = (items, userLocations) => {
  const itemsList = items.map((item, index) => 
    `${index + 1}. ${item.description} ${item.brand ? `(${item.brand})` : ''} ${item.vendor ? `[${item.vendor}]` : ''}`
  ).join('\n');

  return `
Please categorize these inventory items and assign storage locations with INTELLIGENT MATCHING:

ITEMS TO CATEGORIZE:
${itemsList}

AVAILABLE CATEGORIES:
${INVENTORY_CATEGORIES.join(', ')}

AVAILABLE LOCATIONS:
${userLocations.join(', ')}

**INTELLIGENT CATEGORY & LOCATION MATCHING GUIDELINES**

When analyzing each item, use its name and context to infer the correct category and find a matching storage location. Use the rules below to guide classification:

🔑 **Match categories and locations using keyword patterns:**

**Meat**
- Keywords: beef, pork, chicken, lamb, sausage, steak, brisket
- Locations: "Meat Locker", "Meat Storage", "Butcher Room", "Walk-in Freezer"

**Dairy** (includes eggs — eggs require refrigeration and should NEVER go in Pantry/Dry Storage)
- Keywords: milk, cheese, butter, cream, yogurt, eggs, egg
- Locations: "Dairy Cooler", "Walk-in Cooler", "Walk-in Fridge", "Dairy Storage"

**Seafood**
- Keywords: shrimp, salmon, fish, cod, scallop, crab
- Locations: "Seafood Freezer", "Meat Locker", "Walk-in Freezer"

**Non-Alcoholic Beverage**
- Keywords: juice, soda, tea, water, sparkling, kombucha
- Locations: "Beverage Cooler", "Dry Storage", "Bar Area"

**Liquor**
- Keywords: vodka, whiskey, rum, tequila, gin, brandy
- Locations: "Liquor Storage", "Bar Area"

**Wine**
- Keywords: cabernet, chardonnay, merlot, pinot, zinfandel
- Locations: "Wine Cellar", "Bar Area"

**Beer**
- Keywords: lager, ale, IPA, pilsner, stout, beer
- Locations: "Beer Cooler", "Bar Area"

**Coffee**
- Keywords: coffee, espresso, decaf, roast
- Locations: "Coffee Storage", "Dry Storage", "Bar Area"

**Produce**
- Keywords: lettuce, tomato, onion, apple, berry, melon, greens
- Locations: "Produce Cooler", "Walk-in Cooler", "Dry Storage" (if shelf-stable)

**Baked Goods**
- Keywords: bread, buns, rolls, pastries, muffins, cake
- Locations: "Bakery Storage", "Dry Storage", "Pantry"

**Retail**
- Keywords: merchandise, retail, packaged snacks, bottled goods
- Locations: "Retail Shelf", "Dry Storage", "Retail Display"

🧠 **Matching Logic:**
1. **Identify the item's core type** (e.g., "chicken breast" → meat)
2. **Use keywords to find the most specific matching location** from the provided options
3. **Avoid generic locations if a specific match is available**
4. **Use best judgment if the item fits multiple categories** — prioritize food safety and usage context

**CRITICAL: NEVER default to the first location in the list. Always analyze the item type first!**

🔍 **LOCATION SELECTION RULE: Match items to the **most semantically specific** location name that fits the product.**

- For example:
  - "Cheddar Cheese" → "Cheese Room" (more specific than "Walk-in Cooler")
  - "Cabernet Sauvignon" → "Wine Cellar" (better than "Liquor Storage" or "Bar Area")
  - "Beef Brisket" → "Meat Locker" (better than "Walk-in Freezer" if both exist)
  - "Cleaning Spray" → "Janitorial Closet" (better than "Dry Storage")

❗ **If multiple valid locations exist:**
  - Choose the one **most uniquely intended** for that item type.
  - Avoid generic locations if a more targeted one exists.
  - **Think about what the location name implies.**

If unsure between two, pick the one whose name most clearly signals the item type (e.g., "Wine" → "Wine Cellar", not "Liquor Storage").

**EXAMPLES OF GOOD LOCATION MATCHING:**
- "Beef Brisket" → "Meat Locker" (if available) or "Walk-in Freezer"
- "Whole Milk" → "Dairy Cooler" (if available) or "Walk-in Cooler"
- "Cabernet Wine" → "Wine Cellar" (if available) or "Bar Area"
- "Paper Towels" → "Paper Storage" (if available) or "Dry Storage"

INSTRUCTIONS:
1. Assign the most appropriate category from the available categories
2. **CAREFULLY ANALYZE each item and assign the MOST APPROPRIATE storage location**
3. Consider temperature requirements, food safety, and storage efficiency
4. If an item doesn't fit existing categories, suggest the closest match

RESPONSE FORMAT (JSON):
{
  "categorized_items": [
    {
      "index": 1,
      "description": "item description",
      "suggested_category": "category name",
      "suggested_location": "location name",
      "reasoning": "brief explanation of why this location was chosen"
    }
  ]
}

Please respond with valid JSON only.
`;
};

/**
 * Parse AI response and merge with original items
 */
const parseAIResponse = (aiResponse, originalItems) => {
  try {
    // Clean the response to extract JSON
    const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No valid JSON found in AI response');
    }
    
    const parsed = JSON.parse(jsonMatch[0]);
    const categorizedItems = parsed.categorized_items || [];
    
    return originalItems.map((item, index) => {
      const aiSuggestion = categorizedItems.find(ai => ai.index === index + 1);
      
      return {
        ...item,
        ai_suggested_category: aiSuggestion?.suggested_category || null,
        ai_suggested_location: aiSuggestion?.suggested_location || null,
        ai_reasoning: aiSuggestion?.reasoning || null,
        // Keep original values if they exist
        category: item.category || aiSuggestion?.suggested_category || null,
        location: item.location || aiSuggestion?.suggested_location || null
      };
    });
    
  } catch (error) {
    console.error('Error parsing AI response:', error);
    console.log('Raw AI response:', aiResponse);
    
    // Return original items with error flag
    return originalItems.map(item => ({
      ...item,
      ai_error: 'Failed to parse AI response'
    }));
  }
};

/**
 * Test function to validate API connection
 */
export const testClaudeConnection = async () => {
  try {
    const testItems = [
      { description: 'Whole Milk - 1 Gallon', brand: 'Organic Valley', vendor: 'Sysco' },
      { description: 'Ground Beef - 80/20', brand: '', vendor: 'US Foods' },
      { description: 'Romaine Lettuce', brand: '', vendor: 'Local Farm' }
    ];
    
    const result = await categorizeInventoryItems(testItems);
    console.log('Claude API Test Result:', result);
    return result;
    
  } catch (error) {
    console.error('Claude API Test Failed:', error);
    throw error;
  }
};

// Named exports
export { INVENTORY_CATEGORIES, DEFAULT_LOCATIONS };

export default {
  categorizeInventoryItems,
  testClaudeConnection,
  INVENTORY_CATEGORIES,
  DEFAULT_LOCATIONS
};
